'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { CONVERSION_EVENTS, pushEvent } from '@/lib/analytics/core';
import {
  SEO_CTA_COOKIE_MAX_AGE_SECONDS, SEO_CTA_COOKIE_NAME, SEO_CTA_EXPERIMENT_ID,
  chooseSeoCtaVariant, isEligibleSeoCtaPath, loadSeoCtaAssignment,
  seoCtaEventProperties, type SeoCtaAssignment,
} from '@/lib/seo/cta-experiment';

export function SeoCtaExperiment({ active }: { active: boolean }) {
  const pathname = usePathname();
  const eligible = active && isEligibleSeoCtaPath(pathname);
  const [assignment, setAssignment] = useState<SeoCtaAssignment | null>(null);

  useEffect(() => {
    if (!eligible) return;
    let current = loadSeoCtaAssignment();
    if (!current) {
      const variant = chooseSeoCtaVariant();
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${encodeURIComponent(SEO_CTA_COOKIE_NAME)}=${variant};Path=/;Max-Age=${SEO_CTA_COOKIE_MAX_AGE_SECONDS};SameSite=Lax${secure}`;
      current = { experimentId: SEO_CTA_EXPERIMENT_ID, variant };
    }
    const frame = window.requestAnimationFrame(() => setAssignment(current));
    const exposureKey = `${SEO_CTA_EXPERIMENT_ID}:exposed:${pathname}`;
    if (!sessionStorage.getItem(exposureKey)) {
      pushEvent('seo_cta_exposure', { ...seoCtaEventProperties(current), page_path: pathname });
      sessionStorage.setItem(exposureKey, '1');
    }
    return () => window.cancelAnimationFrame(frame);
  }, [eligible, pathname]);

  if (!eligible || !assignment) return null;
  const props = seoCtaEventProperties(assignment);
  return (
    <aside aria-label="Try BizRnR" data-testid="seo-cta-experiment"
      data-experiment-id={assignment.experimentId} data-experiment-variant={assignment.variant}
      className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-xl items-center justify-between gap-3 rounded-2xl border border-cyan-400/30 bg-slate-950/95 p-3 text-white shadow-2xl backdrop-blur md:bottom-5">
      <p className="hidden text-sm text-slate-200 sm:block">
        {assignment.variant === 'B' ? 'Hear the AI receptionist before you subscribe.' : 'Never miss another customer call.'}
      </p>
      <div className="ml-auto flex items-center gap-2">
        {assignment.variant === 'B' && (
          <Link href="/#voice" className="rounded-lg border border-cyan-300/50 px-3 py-2 text-sm font-semibold text-cyan-100"
            onClick={() => pushEvent(CONVERSION_EVENTS.DEMO_CTA_CLICKED, { ...props, cta_location: 'seo_sticky' })}>
            Try the live demo
          </Link>
        )}
        <Link href="/auth/register?plan=spark" className="rounded-lg bg-cyan-400 px-4 py-2 text-sm font-bold text-slate-950"
          onClick={() => pushEvent(CONVERSION_EVENTS.SEO_CTA_CLICKED, { ...props, cta_location: 'seo_sticky', cta_action: 'subscribe' })}>
          Start for $99
        </Link>
      </div>
    </aside>
  );
}
